import { ErrorHandlerResult, instanceOfMaintenanceModeExceptionDetails } from '@/api/generator';
import { notificationForError } from '@/components/notifications/utils';
import { routenames } from '@/router/routenames.ts';
import { routerInstance } from '@/router/router.ts';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useSystemStore } from '@/stores/systemStore';
import { VeeFormContext } from '@/components/forms/useVeeForm';
import {
  AuthenticationError,
  ConcurrencyError,
  ForbiddenError,
  isForbiddenError,
  isNotFoundError,
  isSemanticError,
  isServerError,
  isSyntacticError,
  NotFoundError,
  ServerError,
} from './errors';
import { errorFlowAsync } from './handle';
import { ApiError } from './errors/ApiError';
import { Router } from 'vue-router';
import { SKIP_FORM_CHECK } from '@/components/confirmationModals/useFormConfirmationModal';
import { mapBackendErrorToAjvErrors } from '@/validation/validators/backendValidator';
import { Level, trackException } from '@/telemetry/appInsights';

/**
 * Stop the error flow with this handler, only do this if you're sure you handle all relevant errors before using other handlers or it might hide errors
 */
export function stopErrorPropagation() {
  return undefined;
}

/**
 * Marks intentionally superseded API cancellations so the default handler can ignore them safely.
 */
function isAbortError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const name = 'name' in error ? String(error.name) : undefined;
  if (name === 'AbortError') {
    return true;
  }

  const cause = 'cause' in error ? error.cause : undefined;
  if (!cause || cause === error || typeof cause !== 'object') {
    return false;
  }

  const causeName = 'name' in cause ? String(cause.name) : undefined;
  return causeName === 'AbortError';
}

export function ignoreCancellationErrorHandler(error: Error) {
  // Abort can surface either as FetchError(cause = AbortError) or as a direct AbortError
  // depending on browser/runtime timing. Treat both as intentional cancellation.
  if (isAbortError(error)) {
    return undefined;
  }

  return error;
}

/**
 * Logs error with class specific method or fallback, as universal handler to be mapped to Error classes
 * @param {Error} error to hanlde
 */
async function logConsoleErrorHandler(error: Error) {
  console.error(error.message);
  return error;
}

async function signOutErrorHandler(error: Error) {
  routerInstance.push({
    name: routenames.signOut,
    hash: `#${SKIP_FORM_CHECK}`,
    query: { redirect: window.location.href },
  });
  return error;
}

/**
 * Toasts error with class specific method or fallback, as universal handler to be mapped to Error classes
 * @param {Error} error to hanlde
 */
async function notifyErrorHandler(error: Error) {
  useNotificationStore().notify(notificationForError(error), { store: true });
  return error;
}

/**
 * Add a NotificationEntry to the store without triggering a notification right away
 * @param error
 */
export async function catchAllErrorHandler(error: Error) {
  const notificationEntry = notificationForError(error);
  useNotificationStore().addMessage(notificationEntry);
  return undefined;
}

async function updatePermissionsErrorHandler(error: Error) {
  await usePermissionsStore().fetchMyPermissions();
  return error;
}

export function mayHaveValidationErrors(error: Error) {
  return isSemanticError(error) || isSyntacticError(error) || isForbiddenError(error);
}

export function hasValidationErrors(error: Error) {
  return mayHaveValidationErrors(error) && !!error.errorResponse?.validationErrors?.length;
}

export function ignoreValidationErrorHandler(error: Error) {
  return hasValidationErrors(error) ? undefined : error;
}

/**
 * Factory function for error handler that passes error information to form to be shown at the field for the path in the validated object of type T
 * @param {VeeFormContext<T>} form
 * @param propagateValidationErrors
 */
export function formBackendErrorHandler(form: VeeFormContext, propagateValidationErrors = false) {
  return (error: Error) => {
    const aggregatedErrors = mapBackendErrorToAjvErrors(error);

    if (aggregatedErrors?.length) {
      aggregatedErrors?.forEach((aggregatedError) => {
        form.setFieldError(aggregatedError.path, aggregatedError.errors[0]);
      });

      return propagateValidationErrors ? error : undefined;
    } else {
      return error;
    }
  };
}

export function entityNotFoundErrorHandler(router: Router) {
  return async (error: Error) => {
    if (isNotFoundError(error) || isSyntacticError(error) || isSemanticError(error)) {
      await router.push({ name: routenames.notFound, hash: `#${SKIP_FORM_CHECK}` });
    } else {
      return error;
    }
  };
}

/**
 * Will be thrown from backend, when user is logged in, but application has switched into maintenance mode
 */
async function maintenanceModeErrorHandler(error: Error) {
  if (
    isServerError(error) &&
    instanceOfMaintenanceModeExceptionDetails(error.errorResponse?.details)
  ) {
    const systemStore = useSystemStore();
    await systemStore.fetchMaintenanceModeInfo();
    await routerInstance.push({ name: routenames.signOut });
  } else {
    return error;
  }
}

function trackErrorHandler(error: Error) {
  trackException(
    error,
    {
      detail: `${error.constructor.name}`,
    },
    Level.Error,
  );
  return error;
}

/**
 * Map error class to chain of handler functions to call as default on occurence
 * @param {Error} error to handle
 */

export function defaultErrorHandler(error: Error): ErrorHandlerResult {
  // expected control for intentional call cancellation
  const handlers = (() => {
    switch (error.constructor) {
      case AuthenticationError:
        return [logConsoleErrorHandler, notifyErrorHandler, signOutErrorHandler, trackErrorHandler];

      case ConcurrencyError:
        return [logConsoleErrorHandler, trackErrorHandler];

      case ForbiddenError:
        return [
          logConsoleErrorHandler,
          notifyErrorHandler,
          updatePermissionsErrorHandler,
          trackErrorHandler,
        ];

      case ServerError:
        return [
          maintenanceModeErrorHandler,
          logConsoleErrorHandler,
          notifyErrorHandler,
          trackErrorHandler,
        ];

      case NotFoundError:

      case ApiError:

      default:
        return [logConsoleErrorHandler, notifyErrorHandler, trackErrorHandler];
    }
  })();

  return errorFlowAsync(...handlers)(error);
}
